import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, MapPinIcon, BuildingIcon, CalendarIcon, ChevronDownIcon, PencilIcon, SearchIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { trendData, quotesListData, userInsights } from '../../data/biData';
const COLORS = ['#10b981', '#3b82f6', '#ef4444', '#64748b'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const salesManagersByRegion: Record<string, {
  name: string;
  email: string;
  initials: string;
}[]> = {
  'Aisco MENA': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }, {
    name: 'Mohamed S. Aloraini',
    email: 'mohamed.aloraini@aisco.com',
    initials: 'MA'
  }, {
    name: 'Abdullah ALkaff',
    email: 'abdullah.alkaff@aisco.com',
    initials: 'AA'
  }],
  'Aisco AFRICA': [{
    name: 'Khaled Shalabi',
    email: 'khaled.shalabi@aisco.com',
    initials: 'KS'
  }, {
    name: 'Baher Fahem Alani',
    email: 'baher.alani@aisco.com',
    initials: 'BA'
  }, {
    name: 'Essam Fakhry Mohamed',
    email: 'essam.mohamed@aisco.com',
    initials: 'EM'
  }],
  'Aisco EUROPE': [{
    name: 'Régis SIMIER',
    email: 'regis.simier@aisco.com',
    initials: 'RS'
  }, {
    name: 'Javier Adrados',
    email: 'javier.adrados@aisco.com',
    initials: 'JA'
  }, {
    name: 'Daniel Fernández',
    email: 'daniel.fernandez@aisco.com',
    initials: 'DF'
  }, {
    name: 'Olexandr Morozov',
    email: 'olexandr.morozov@aisco.com',
    initials: 'OM'
  }],
  'Aisco AMERICA': [{
    name: 'Diego Solis',
    email: 'diego.solis@aisco.com',
    initials: 'DS'
  }, {
    name: 'Chris Spelic',
    email: 'chris.spelic@aisco.com',
    initials: 'CS'
  }, {
    name: 'Mark Ellis',
    email: 'mark.ellis@aisco.com',
    initials: 'ME'
  }],
  'Aisco ANZ': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }]
};
const allRegions = Object.keys(salesManagersByRegion);
export function UserPerformanceView() {
  const [selectedRegion, setSelectedRegion] = useState('Aisco AMERICA');
  const [selectedManagerName, setSelectedManagerName] = useState('Diego Solis');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  const currentManager = Object.values(salesManagersByRegion).flat().find(m => m.name === selectedManagerName) || salesManagersByRegion['Aisco AMERICA'][0];
  const managersForRegion = selectedRegion === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[selectedRegion] || [];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
    }
    if (isDropdownOpen || isEditingDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isEditingDate]);
  const handleSelectManager = (manager: {
    name: string;
    email: string;
    initials: string;
  }, region: string) => {
    setSelectedManagerName(manager.name);
    if (region !== selectedRegion && selectedRegion !== 'All') {
      setSelectedRegion(region);
    }
    setIsDropdownOpen(false);
  };
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    const managers = region === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[region] || [];
    if (managers.length > 0 && !managers.find(m => m.name === selectedManagerName)) {
      setSelectedManagerName(managers[0].name);
    }
  };
  const statusData = [{
    name: 'Won',
    value: 85
  }, {
    name: 'Active',
    value: 15
  }, {
    name: 'Lost',
    value: 15
  }, {
    name: 'Draft',
    value: 5
  }];
  const userTrendData = trendData.map(d => ({
    ...d,
    total: Math.floor(d.total * 0.15),
    won: Math.floor(d.won * 0.15),
    lost: Math.floor(d.lost * 0.15)
  }));
  return <div className="space-y-6" data-id="element-966" data-mp="3sbaqzd8apcnk">
      {/* Access/Coverage Block */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-967" data-mp="3ecb5geg2xdvm">
        {/* User Identity — now a selector */}
        <div className="flex items-center gap-4 mr-auto relative" ref={dropdownRef} data-id="element-968" data-mp="3idmxeqmfeb6z">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-2 ring-blue-400/30 shadow-lg flex-shrink-0" data-id="element-969" data-mp="3ibjzzliy1fs8">
            <span className="text-lg font-bold text-white" data-id="element-970" data-mp="39dgkb5un3d8c">
              {currentManager.initials}
            </span>
          </div>
          <div data-id="element-971" data-mp="17llg1bapqb05">
            {/* Clickable name selector */}
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 group text-left" data-id="element-972" data-mp="17l272k31iwy9">
              <h3 className="text-white font-bold text-xl leading-tight group-hover:text-blue-200 transition-colors" data-id="element-973" data-mp="115l8u3r3pdkp">
                {currentManager.name}
              </h3>
              <ChevronDownIcon size={18} className={`text-blue-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-974" data-mp="w6qrl68s7x2p" />
            </button>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-975" data-mp="1ou4gch2btmbq">
              Territory Manager
            </p>
            <p className="text-blue-300/50 text-xs leading-tight mt-0.5" data-id="element-976" data-mp="1jwl23h5mp9js">
              {currentManager.email}
            </p>

            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-977" data-mp="1a71ksfxkdnr1">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-978" data-mp="1a4fa47fdms4l">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-979" data-mp="22tma7auhbpry" />
                <span className="font-medium" data-id="element-980" data-mp="2b55xo18arwsh">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-981" data-mp="2f4eooybydgkc" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-982" data-mp="2p3ma88suchij">
                  <div className="space-y-2.5" data-id="element-983" data-mp="2u0cpitwtn92w">
                    {/* Period type */}
                    <div data-id="element-984" data-mp="2y27se2uehuis">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-985" data-mp="32ur38s1pebjw">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-986" data-mp="37sixhsptm9r9">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-987" data-mp="3curpomuvd7sb">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-988" data-mp="azyxl6ilf0tc">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-989" data-mp="3jez8inppbstu">
                          Month
                        </p>
                        <div className="relative" data-id="element-990" data-mp="3feyuh46iljnb">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-991" data-mp="3t5c0v23rcqaj">
                            {months.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-992" data-mp="2von2xjhxi86f">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-993" data-mp="1fyvu8eztqx5b">
                            <ChevronDownIcon size={12} data-id="element-994" data-mp="124utxgt8522u" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-995" data-mp="1ywyiusogms2l">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-996" data-mp="1yx8462yk8b1y">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-997" data-mp="1pwa0xmvy47kf">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-998" data-mp="1pufg08kocfgc">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-999" data-mp="2hgbzjyv82sqz">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-1000" data-mp="2hf0w605ovn0e">
                        Year
                      </p>
                      <div className="relative" data-id="element-1001" data-mp="28f5hhlcxd9vm">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-1002" data-mp="28ecix7o4dqok">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-1003" data-mp="23hvsu7xd128u">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-1004" data-mp="1mlddlmvrb4i">
                          <ChevronDownIcon size={12} data-id="element-1005" data-mp="1h7vpazzshsdi" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl z-50 min-w-[340px] max-h-[420px] overflow-hidden flex flex-col" data-id="element-1006" data-mp="1r48mw8yggqdj">
                {/* Region filter tabs */}
                <div className="px-3 pt-3 pb-2 border-b border-white/10" data-id="element-1007" data-mp="200hj7er4z8vg">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-2" data-id="element-1008" data-mp="2euvxj1zr9u3s">
                    Region
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-1009" data-mp="kh3a04kok4cs">
                    <button onClick={() => handleRegionChange('All')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === 'All' ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-1010" data-mp="khmu9rxsz4tz">
                      All
                    </button>
                    {allRegions.map(region => <button key={region} onClick={() => handleRegionChange(region)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === region ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-1011" data-mp="1e7dkq58x0pfb">
                        {region.replace('Aisco ', '')}
                      </button>)}
                  </div>
                </div>

                {/* Search bar */}
                <div className="px-3 pt-2 pb-1 border-b border-white/10" data-id="element-1012" data-mp="1wy4yrk3ul401">
                  <div className="relative" data-id="element-1013" data-mp="21vmx6gk8o0ts">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-400/50" data-id="element-1014" data-mp="21yiwo9bqixkc" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or email..." className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-blue-300/40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" autoFocus data-id="element-1015" data-mp="2buvqr8b6zdfm" />
                  </div>
                </div>

                {/* Sales managers list */}
                <div className="overflow-y-auto flex-1 py-1 no-scrollbar" data-id="element-1016" data-mp="3hh75c08sdlgx">
                  {selectedRegion === 'All' ? Object.entries(salesManagersByRegion).map(([region, managers]) => {
                const filtered = managers.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase()));
                if (filtered.length === 0) return null;
                return <div key={region} data-id="element-1017" data-mp="479nbhnyku66">
                              <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-wider text-blue-400/50 font-medium" data-id="element-1018" data-mp="11pjhcubr33a9">
                                {region}
                              </p>
                              {filtered.map(manager => <button key={`${region}-${manager.name}`} onClick={() => handleSelectManager(manager, region)} className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-1019" data-mp="hegpwmptyxs5">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-1020" data-mp="ch7199a9mq5d">
                                    <span className="text-[10px] font-bold text-white" data-id="element-1021" data-mp="155m2mt0h59y8">
                                      {manager.initials}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-1022" data-mp="vdyi7c2c4t69">
                                    <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-1023" data-mp="1j4lvkcv0sb07">
                                      {manager.name}
                                    </p>
                                    <p className="text-xs text-blue-300/50 truncate" data-id="element-1024" data-mp="38kejdjni1u43">
                                      {manager.email}
                                    </p>
                                  </div>
                                  {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-1025" data-mp="3de0p0rv8wxqe" />}
                                </button>)}
                            </div>;
              }) : managersForRegion.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase())).map(manager => <button key={manager.name} onClick={() => handleSelectManager(manager, selectedRegion)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-1026" data-mp="2ypebmvp6ig1t">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-1027" data-mp="9vh64fygj489">
                              <span className="text-[10px] font-bold text-white" data-id="element-1028" data-mp="37pk75mv1opb4">
                                {manager.initials}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1029" data-mp="3gq8lyjs9itp2">
                              <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-1030" data-mp="3gppddtoi5hnp">
                                {manager.name}
                              </p>
                              <p className="text-xs text-blue-300/50 truncate" data-id="element-1031" data-mp="2t167fksxdgys">
                                {manager.email}
                              </p>
                            </div>
                            {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-1032" data-mp="218owiklcdb8w" />}
                          </button>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Assigned Business Unit Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-1033" data-mp="1xpq572dwjtf2">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-1034" data-mp="1oqw3ixlpscun">
            Assigned Business Units
          </p>
          <div className="flex gap-2" data-id="element-1035" data-mp="1jsuja9cjy7hl">
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-1036" data-mp="1jrrwacicop6y">
              Aisco AMERICA
            </span>
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-1037" data-mp="1a16wwmuurgmz">
              Aisco LATAM
            </span>
          </div>
        </div>

        {/* Coverage Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-1038" data-mp="113egffdea2y0">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-1039" data-mp="r3dt7icen2oj">Coverage</p>
          <div className="flex gap-4" data-id="element-1040" data-mp="r5gtcv79uc6z">
            <div className="flex items-center text-sm text-blue-200" data-id="element-1041" data-mp="2ezjzm8cg3hy8">
              <MapPinIcon size={14} className="mr-1.5 text-blue-400" data-id="element-1042" data-mp="2jz6fyqnkleb9" />
              <span className="font-semibold text-white mr-1" data-id="element-1043" data-mp="2jydh0buurt3o">4</span> Countries
            </div>
            <div className="flex items-center text-sm text-blue-200" data-id="element-1044" data-mp="25z41leeh130r">
              <BuildingIcon size={14} className="mr-1.5 text-blue-400" data-id="element-1045" data-mp="2axfc7rclbhk1" />
              <span className="font-semibold text-white mr-1" data-id="element-1046" data-mp="1w9u8pwbszgbl">12</span> Dealers
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1047" data-mp="212dmoueekdgx">
        <BIKPI title="Total" value={120} trend={12} color="blue" data-id="element-1048" data-mp="1na799g2mwkl6" />
        <BIKPI title="Won" value={85} trend={8} color="emerald" data-id="element-1049" data-mp="1r9zizw3rkws4" />
        <BIKPI title="Win Rate" value="85.0%" trend={2.5} color="teal" data-id="element-1050" data-mp="1r84uk7taamja" />
        <BIKPI title="Active" value={15} color="amber" data-id="element-1051" data-mp="1db84i8kzp93g" />
        <BIKPI title="Lost" value={15} color="red" data-id="element-1052" data-mp="1iacev2jnhcvd" />
        <BIKPI title="Draft" value={5} color="purple" data-id="element-1053" data-mp="1ibvwoxkyfvz9" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1054" data-mp="yo9fek9luqrw">
        <BICard title="Quotes by Status" className="lg:col-span-1" data-id="element-1055" data-mp="tpzncij6g6ai">
          <div className="h-64 flex flex-col items-center justify-center" data-id="element-1056" data-mp="18lxetni5omge">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1057" data-mp="18k48687jm28e">
              <PieChart data-id="element-1058" data-mp="13mb1le2bv67l">
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-1059" data-mp="13lsybxh8zrq6">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-1060" data-mp="1cmr1yenrxx88" data-mpi={index} />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-1061" data-mp="1rbna0bptza8u" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-1062" data-mp="1wcb8fige2td3" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Personal Quotes Trend" className="lg:col-span-2" data-id="element-1063" data-mp="23w7y83x5qoz8">
          <div className="h-64" data-id="element-1064" data-mp="2huxr17cy22hs">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1065" data-mp="2hu4qwp8hqb8m">
              <LineChart data={userTrendData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-1066" data-mp="2cwcrzb9fhoux">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-1067" data-mp="1p4frggbfij94" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-1068" data-mp="1l6ru76m9kee2" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-1069" data-mp="1yyeqv7k4boxd" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-1070" data-mp="1u4aicjv7f8u6" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-1071" data-mp="11ev6jrvptbx7" />
                <Line type="monotone" dataKey="total" name="Total" stroke="#3b82f6" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1072" data-mp="3hurjexm0rwmu" />
                <Line type="monotone" dataKey="won" name="Won" stroke="#10b981" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1073" data-mp="34567u8ajujou" />
                <Line type="monotone" dataKey="lost" name="Lost" stroke="#ef4444" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-1074" data-mp="mwexjyt2xce" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1075" data-mp="3mwgvmgkzagci">
        <div className="lg:col-span-2 space-y-6" data-id="element-1076" data-mp="3rrw6cznovw4p">
          <BICard title="Recent Quotes" data-id="element-1077" data-mp="2bexnc9sjjn73">
            <div className="overflow-x-auto" data-id="element-1078" data-mp="2be61vrsnr5uj">
              <table className="w-full text-sm text-left" data-id="element-1079" data-mp="2bhaar2h2iprj">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-1080" data-mp="2getughwjbuqv">
                  <tr data-id="element-1081" data-mp="1wbeeiuccvqbo">
                    <th className="px-4 py-3 font-medium" data-id="element-1082" data-mp="1wdqskweztgjg">Quote #</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1083" data-mp="1sauhdqrs8mpj">Date</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1084" data-mp="1nj2qtg8mmgze">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-1085" data-mp="1nh9izxma1qpt">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-1086" data-mp="1ik991bmb43tf">Amount</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-1087" data-mp="1dm7r4u0maajj">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1088" data-mp="14k87rv24qudf">
                  {quotesListData.filter(q => q.user === 'Alice Smith').map((quote, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-1089" data-mp="zm6phccj2z30" data-mpi={idx}>
                        <td className="px-4 py-3 font-medium text-blue-400" data-id="element-1090" data-mp="2yz127qra1f3s">
                          {quote.id}
                        </td>
                        <td className="px-4 py-3 text-blue-200" data-id="element-1091" data-mp="2u0fz9jkn4hbn">
                          {quote.date}
                        </td>
                        <td className="px-4 py-3 text-white" data-id="element-1092" data-mp="36i0l7vatt4mm">{quote.dealer}</td>
                        <td className="px-4 py-3 text-blue-300" data-id="element-1093" data-mp="36fxlgjjvfx4p">
                          {quote.country}
                        </td>
                        <td className="px-4 py-3 text-right text-white" data-id="element-1094" data-mp="3g8ljtiqi295j">
                          ${quote.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1095" data-mp="2imo4fu7f2jxt">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${quote.status === 'Won' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : quote.status === 'Active' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : quote.status === 'Lost' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`} data-id="element-1096" data-mp="2nk7jgwbxv0qt">
                            {quote.status}
                          </span>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-1097" data-mp="kbf9j4p68p6e">
          <BIInsights insights={userInsights} data-id="element-1098" data-mp="kcgvi47q6w2w" />
        </div>
      </div>
    </div>;
}